import { create } from "zustand";
import { com } from "../proto/game.pb";

const MAX_BETS = 50;

export type BetEvent = {
  id: string;
  roundId: number;
  userId: number;
  slotId: number;
  amount: number;
  nickname: string;
  avatar: string;
  multiplier: number | null;
  payout: number | null;
  isBigWin?: boolean;
};

type CancelDelta = { roundId?: number; userId: number; slotId?: number };
type BettingCounters = com.achlid.crash.game.ITotalBetsCountUpdate;
type CashoutDelta = {
  roundId?: number;
  userId: number;
  slotId?: number;
  multiplier: number | null;
  payout: number | null;
  isBigWin?: boolean;
  playerName?: string; // Для big win уведомлений
  avatar?: string; // Для big win уведомлений
};

export type BetFeedState = {
  playersBettingCounters: BettingCounters;
  setPlayersBettingCounters: (counters: BettingCounters) => void;
  currentBets: BetEvent[];
  lastRoundX: number | null;
  addNewBets: (bets: BetEvent[] | []) => void;
  applyCancels: (cancels: CancelDelta[]) => void;
  applyCashouts: (cashouts: CashoutDelta[]) => void;
  resetOnPreRound: () => void;
  resetOnCrash: () => void;
  setLastRoundX: (x: number | null) => void;
};

export const useBetFeedStore = create<BetFeedState>((set) => ({
  playersBettingCounters: {
    activeBetsCount: 0,
    cashedOutCount: 0,
    totalCount: 0,
    totalCashedOutAmount: 0,
  },
  lastRoundX: null,
  currentBets: [],
  setPlayersBettingCounters: (counters: BettingCounters) =>
    set(() => ({ playersBettingCounters: counters })),

  setLastRoundX: (lastRoundX: number | null) => set(() => ({ lastRoundX })),
  addNewBets: (bets) =>
    set((state) => {
      const normalized = bets.map((b) => ({
        ...b,
        multiplier: Number((b as any)?.multiplier ?? 0),
        payout: Number((b as any)?.payout ?? 0),
      }));
      const combinedBets = [...normalized, ...state.currentBets].slice(
        0,
        MAX_BETS
      );
      return {
        currentBets: combinedBets,
      };
    }),
  applyCancels: (cancels) =>
    set((state) => {
      const removalKeySet = new Set(
        cancels.map(
          (cancelEntry) => `${cancelEntry.userId}:${cancelEntry.slotId ?? "*"}`
        )
      );
      const remainingBets = state.currentBets.filter((betEntry) => {
        const exactKey = `${betEntry.userId}:${betEntry.slotId}`;
        const wildcardKey = `${betEntry.userId}:*`;
        return !(removalKeySet.has(exactKey) || removalKeySet.has(wildcardKey));
      });
      return {
        currentBets: remainingBets,
      };
    }),
  applyCashouts: (cashouts) =>
    set((state) => {
      const cashoutByKey = new Map<string, CashoutDelta>();
      const bigWinCashouts: CashoutDelta[] = [];

      for (const c of cashouts) {
        // Если это big win (isBigWin === true), добавляем в отдельный массив
        if (c.isBigWin) {
          bigWinCashouts.push(c);
        } else {
          cashoutByKey.set(`${c.userId}:${c.slotId}`, c);
        }
      }

      const updatedBets = state.currentBets.map((betEntry) => {
        const key = `${betEntry.userId}:${betEntry.slotId}`;
        const matchedCashout = cashoutByKey.get(key);
        if (!matchedCashout) return betEntry;
        return {
          ...betEntry,
          multiplier: Number(matchedCashout.multiplier ?? 0),
          payout: Number(matchedCashout.payout ?? 0),
          isBigWin: matchedCashout.isBigWin ?? betEntry.isBigWin,
        };
      });

      // Добавляем big win как новые ставки в начало массива
      const newBets = bigWinCashouts.map((bw) => ({
        id: `${bw.roundId}:${bw.userId}:${bw.slotId}`,
        roundId: bw.roundId ?? 0,
        userId: bw.userId,
        slotId: bw.slotId ?? 0,
        amount: 0,
        nickname: bw.playerName ?? "",
        avatar: bw.avatar ?? "",
        multiplier: Number(bw.multiplier ?? 0),
        payout: Number(bw.payout ?? 0),
        isBigWin: true,
      }));

      const combinedBets = [...newBets, ...updatedBets].slice(0, MAX_BETS);

      return {
        currentBets: combinedBets,
      };
    }),
  resetOnPreRound: () =>
    set(() => ({
      playersBettingCounters: {
        activeBetsCount: 0,
        cashedOutCount: 0,
        totalCount: 0,
        totalCashedOutAmount: 0,
      },
      currentBets: [],
    })),
  resetOnCrash: () =>
    set(() => ({
      playersBettingCounters: {
        activeBetsCount: 0,
        cashedOutCount: 0,
        totalCount: 0,
        totalCashedOutAmount: 0,
      },
      currentBets: [],
    })),
}));
